import { useId, useMemo, useState } from "react";
import type { FormEvent } from "react";

import { useStableRest } from "../../utils/use-stable-rest";
import { EXPORT_TABLE_FORMAT_ICONS, EXPORT_TABLE_FORMATS } from "./export-table-dialog.constants";
import { exportTableScopeDescription } from "./messages";
import type {
  ExportTableDialogProps,
  ExportTableDialogViewModel,
  ExportTableFormat,
  ExportTableOption,
  ExportTableXlsxLayout,
} from "./types-export-table-dialog";

/** ViewModel do ExportTableDialog: escolha de formato/organização, habilitação do Exportar e envio; sem JSX. */
export function useExportTableDialog({
  listName,
  scopeDescription,
  isExporting,
  // Sem efeito (ver `variant` nas opções): sai do `rest` para não chegar ao `Dialog`.
  variant: _variant,
  xlsxLayouts,
  onExport,
  onCancel,
  testId = "export-table",
  className,
  style,
  classNames,
  ...props
}: ExportTableDialogProps): ExportTableDialogViewModel {
  const rest = useStableRest(props);
  const headingId = useId();
  const [format, setFormat] = useState<ExportTableFormat | null>(null);
  const [xlsxLayout, setXlsxLayout] = useState<ExportTableXlsxLayout | null>(null);

  const needsLayout = format === "xlsx" && Boolean(xlsxLayouts?.length);
  // Sem formato escolhido não há o que exportar; o Excel com organização pede a organização.
  const canExport = Boolean(format) && !isExporting && (!needsLayout || Boolean(xlsxLayout));

  function chooseFormat(next: ExportTableFormat) {
    setFormat(next);
    if (next === "xlsx" && xlsxLayouts?.length && !xlsxLayout)
      setXlsxLayout(xlsxLayouts[0]?.value ?? "consolidado");
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!format || (needsLayout && !xlsxLayout)) return;

    onExport(format, format === "xlsx" && xlsxLayout ? { xlsxLayout } : undefined);
  }

  const option = (
    kind: "format" | "xlsx-layout",
    detail: { value: string; label: string; description: string },
    selected: boolean,
    choose: () => void,
    iconName?: ExportTableOption["iconName"],
  ): ExportTableOption => ({
    value: detail.value,
    label: detail.label,
    description: detail.description,
    id: `export-${kind}-${detail.value}`,
    testId: `export-${kind}-${detail.value}`,
    ariaLabel: `${detail.label} — ${detail.description}`,
    selected,
    disabled: isExporting,
    iconName,
    choose,
  });

  return useMemo(
    () => ({
      rest,
      testId,
      className,
      style,
      classNames,
      isExporting,
      dismissableMask: !isExporting,
      exportDisabled: !canExport,
      onCancel,
      submit,
      formId: `${testId}-form`,
      dialogTestId: `${testId}-dialog`,
      cancelTestId: `${testId}-cancel`,
      submitTestId: `${testId}-submit`,
      scopeTestId: `${testId}-scope`,
      formatsTitleId: `${headingId}-formats`,
      layoutsTitleId: `${headingId}-layouts`,
      scopeText: scopeDescription ?? exportTableScopeDescription(listName),
      formats: EXPORT_TABLE_FORMATS.map((detail) =>
        option(
          "format",
          detail,
          format === detail.value,
          () => chooseFormat(detail.value),
          EXPORT_TABLE_FORMAT_ICONS[detail.value],
        ),
      ),
      layouts:
        xlsxLayouts && format === "xlsx"
          ? xlsxLayouts.map((detail) =>
              option("xlsx-layout", detail, xlsxLayout === detail.value, () =>
                setXlsxLayout(detail.value),
              ),
            )
          : null,
    }),
    // `option`, `chooseFormat` e `submit` fecham sobre estas mesmas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      rest,
      testId,
      className,
      style,
      classNames,
      isExporting,
      onCancel,
      onExport,
      canExport,
      headingId,
      scopeDescription,
      listName,
      format,
      xlsxLayout,
      xlsxLayouts,
    ],
  );
}
